import React, { useState, useCallback, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import styles from "./ResumePage.module.css";
import { axiosInstance } from "../../utils/axios";

import { ResumeList, KeywordsSection, SearchResultsSection } from "./components";
import { useResumeUpload, useKeywords, useResumeSearch } from "./hooks";
import MetadataContext from "../../Context/metadataContext";
import useUserData from "../../Context/user/useUserData";
import { toggleBookmarkAPI } from "../../utils/bookmark";


const ResumePage = () => {
  const navigate = useNavigate();
  const { metadata, setMetadata } = useContext(MetadataContext);

  const [selectedFileId, setSelectedFileId] = useState(null);
  const [lastAppliedKeywords, setLastAppliedKeywords] = useState({});
  const [userBookmarks, setUserBookmarks] = useState([]);
  const { user } = useUserData();

  const skipChildSearchRef = useRef(false);

  const handleBookmarkToggle = useCallback(async (item, alreadyBookmarked) => {
    try {
      await toggleBookmarkAPI({
        userId: user?.id,
        jobId: item._id,
        alreadyBookmarked,
      });

      setUserBookmarks((prev) => {
        if (alreadyBookmarked) {
          return prev.filter((b) => b._id !== item._id);
        }
        return [...prev, item];
      });
    } catch (err) {
      console.error("Bookmark toggle failed", err);
    }
  }, [user?.id]);

  const {
    searchCache,
    searchingFile,
    executeSearch,
    getCache,
    hasCache,
    clearCache,
    getFileFilterState,
    setFileFilters,
  } = useResumeSearch();

  const { keywords, initKeywords, setForFile, add, remove, get, clear: clearKeywords } = useKeywords();

  const currentFilterState = useMemo(() => {
    if (!selectedFileId) return { filterOptions: {}, selectedSort: "", groupBy: [] };
    return getFileFilterState(selectedFileId);
  }, [selectedFileId, getFileFilterState]);

  const { filterOptions, selectedSort, groupBy } = currentFilterState;

  const setFilterOptions = useCallback((newFilters, newSort = null, newGroupBy = null) => {
    if (!selectedFileId) return;

    const updatedFilters = typeof newFilters === "function" ? newFilters(filterOptions) : newFilters;
    const sortToUse = newSort ?? selectedSort;
    const groupByToUse = newGroupBy ?? groupBy;

    setFileFilters(selectedFileId, updatedFilters, sortToUse, groupByToUse);
  }, [selectedFileId, filterOptions, selectedSort, groupBy, setFileFilters]);

  const handleSearch = useCallback(async (
    searchParam,
    page = 1,
    shouldPassQuery = true,
    sortOverride = null,
    filtersOverride = undefined,
    groupByOverride = null
  ) => {
    if (!selectedFileId) return;

    if (skipChildSearchRef.current) {
      skipChildSearchRef.current = false;
      return;
    }

    const fileKeywords = get(selectedFileId);
    if (!fileKeywords || fileKeywords.length === 0) return;

    const cache = getCache(selectedFileId);
    const sortToUse = sortOverride !== null ? sortOverride : selectedSort;
    const filtersToUse = filtersOverride !== undefined ? filtersOverride : filterOptions;
    const groupByToUse = groupByOverride !== null ? groupByOverride : groupBy;

    if (sortOverride !== null || filtersOverride !== undefined || groupByOverride !== null) {
      setFileFilters(selectedFileId, filtersToUse, sortToUse, groupByToUse);
    }

    const result = await executeSearch(selectedFileId, fileKeywords, {
      page,
      filters: filtersToUse,
      sortBy: sortToUse,
      queryUUID: shouldPassQuery ? cache?.queryUUID : null,
      groupBy: groupByToUse,
    });

    if (result?.filters) {
      setMetadata({ filters: result.filters });
    }
  }, [selectedFileId, get, getCache, executeSearch, filterOptions, selectedSort, groupBy, setMetadata, setFileFilters]);

  const handleGroupByChange = useCallback((newGroupBy) => {
    if (!selectedFileId) return;

    skipChildSearchRef.current = true;

    setFileFilters(selectedFileId, filterOptions, selectedSort, newGroupBy);

    const fileKeywords = get(selectedFileId);
    if (!fileKeywords || fileKeywords.length === 0) return;

    const cache = getCache(selectedFileId);

    executeSearch(selectedFileId, fileKeywords, {
      page: 1,
      filters: filterOptions,
      sortBy: selectedSort,
      queryUUID: cache?.queryUUID,
      groupBy: newGroupBy,
    }).then((result) => {
      if (result?.filters) setMetadata({ filters: result.filters });
    });
  }, [selectedFileId, filterOptions, selectedSort, get, getCache, executeSearch, setFileFilters, setMetadata]);

  const handleUploadSuccess = useCallback((data) => {
    if (data.type === "initial") {
      initKeywords(data.keywords);
      setLastAppliedKeywords(data.keywords);

      if (data.files.length > 0) {
        const firstId = data.files[0].id;
        setSelectedFileId(firstId);
        const firstKeywords = data.keywords[firstId] || [];
        if (firstKeywords.length > 0) {
          executeSearch(firstId, firstKeywords).then((result) => {
            if (result?.filters) setMetadata({ filters: result.filters });
          });
        }
      }
    } else if (data.type === "upload") {
      setForFile(data.fileId, data.keywords);
      setSelectedFileId(data.fileId);
      setLastAppliedKeywords((prev) => ({
        ...prev,
        [data.fileId]: data.keywords || []
      }));

      if (data.keywords?.length > 0) {
        executeSearch(data.fileId, data.keywords).then((result) => {
          if (result?.filters) setMetadata({ filters: result.filters });
        });
      }
    }
  }, [initKeywords, setForFile, executeSearch, setMetadata]);

  const { uploadedFiles, uploadingFiles, uploadErrors, isInitialLoading, handleFileSelect, removeFile } = useResumeUpload(handleUploadSuccess);

  const handleSelectFile = useCallback((fileId) => {
    setSelectedFileId(fileId);

    const fileKeywords = get(fileId);
    if (fileKeywords.length > 0 && !hasCache(fileId)) {
      executeSearch(fileId, fileKeywords).then((result) => {
        if (result?.filters) setMetadata({ filters: result.filters });
      });
    } else {
      const cache = getCache(fileId);
      if (cache?.filters) setMetadata({ filters: cache.filters });
    }
  }, [get, hasCache, executeSearch, getCache, setMetadata]);

  const handleRemoveFile = useCallback((fileId) => {
    removeFile(fileId);
    clearKeywords(fileId);
    clearCache(fileId);

    setLastAppliedKeywords((prev) => {
      const { [fileId]: _, ...rest } = prev;
      return rest;
    });

    if (selectedFileId === fileId) {
      const remaining = uploadedFiles.filter((f) => f.id !== fileId);
      setSelectedFileId(remaining.length > 0 ? remaining[0].id : null);
    }
  }, [removeFile, clearKeywords, clearCache, selectedFileId, uploadedFiles]);

  const handleAddKeyword = useCallback((keyword) => {
    if (!selectedFileId) return false;
    return add(selectedFileId, keyword);
  }, [selectedFileId, add]);

  const handleRemoveKeyword = useCallback((keyword) => {
    if (!selectedFileId) return;
    remove(selectedFileId, keyword);
  }, [selectedFileId, remove]);

  const handleApply = useCallback(async () => {
    if (!selectedFileId) return;

    const fileKeywords = get(selectedFileId);
    if (fileKeywords.length === 0) return;

    setLastAppliedKeywords((prev) => ({
      ...prev,
      [selectedFileId]: [...fileKeywords]
    }));

    setFileFilters(selectedFileId, {}, "", []);

    const [, searchResult] = await Promise.all([
      axiosInstance.post('/search/update_keywords', {
        doc_id: selectedFileId,
        keyword: fileKeywords,
        user_id: user.id
      }).catch(() => {}),

      executeSearch(selectedFileId, fileKeywords, {
        page: 1,
        filters: {},
        sortBy: "",
        queryUUID: null,
        groupBy: [],
      })
    ]);

    if (searchResult?.filters) setMetadata({ filters: searchResult.filters });
  }, [selectedFileId, get, executeSearch, setMetadata, setFileFilters, user.id]);

  const selectedFile = uploadedFiles.find((f) => f.id === selectedFileId);
  const selectedKeywords = selectedFileId ? get(selectedFileId) : [];
  const selectedCache = selectedFileId ? getCache(selectedFileId) : null;
  const isSearching = searchingFile === selectedFileId;
  const hasUploadError = selectedFileId && uploadErrors[selectedFileId];

  const lastApplied = selectedFileId ? (lastAppliedKeywords[selectedFileId] || []) : [];
  const hasUnappliedChanges = selectedKeywords.length > 0 &&
    JSON.stringify([...selectedKeywords].sort()) !== JSON.stringify([...lastApplied].sort());

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate("/")}>
          <IoArrowBack />
          <span>Back to Search</span>
        </button>
        <h1 className={styles.pageTitle}>Agentic AI Search</h1>
      </div>

      <div className={styles.content}>
        <ResumeList
          uploadedFiles={uploadedFiles}
          uploadingFiles={uploadingFiles}
          uploadErrors={uploadErrors}
          selectedFileId={selectedFileId}
          searchCache={searchCache}
          searchingFile={searchingFile}
          isInitialLoading={isInitialLoading}
          onFileSelect={handleFileSelect}
          onSelectFile={handleSelectFile}
          onRemoveFile={handleRemoveFile}
        />

        <div className={styles.rightSection}>
          <div className={styles.sectionHeader}>
            <h2>Extracted Keywords</h2>
            {selectedFile && (
              <span className={styles.selectedFileName}>
                {selectedFile.name}
                {isSearching && <span className={styles.searchingLabel}> - Searching...</span>}
              </span>
            )}
          </div>

          {isInitialLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading resumes...</p>
            </div>
          ) : !selectedFileId ? (
            <div className={styles.noFileSelected}>
              <p>Upload or select a resume to view extracted keywords</p>
            </div>
          ) : hasUploadError ? (
            <div className={styles.uploadError}>
              <p>Upload failed: {uploadErrors[selectedFileId]}</p>
              <p>Please try again or select a different file.</p>
            </div>
          ) : (
            <>
              <KeywordsSection
                keywords={selectedKeywords}
                isLoading={isSearching}
                onAddKeyword={handleAddKeyword}
                onRemoveKeyword={handleRemoveKeyword}
                onApply={handleApply}
                hasUnappliedChanges={hasUnappliedChanges}
              />

              <SearchResultsSection
                searchData={selectedCache}
                isLoading={isSearching}
                filterOptions={filterOptions}
                setFilterOptions={setFilterOptions}
                selectedSort={selectedSort}
                onSearch={handleSearch}
                metadata={metadata}
                groupBy={groupBy}
                setGroupBy={handleGroupByChange}
                userBookmarks={userBookmarks}
                onBookmarkToggle={handleBookmarkToggle}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumePage;